import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { getProductByPriceId } from '../stripe-config';

interface Subscription {
  customer_id: string;
  subscription_id: string | null;
  subscription_status: string;
  price_id: string | null;
  current_period_start: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

interface TemplateAccess {
  id: string;
  template_id: string;
  purchased_at: string;
  expires_at: string;
  access_type: string;
}

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [templateAccess, setTemplateAccess] = useState<TemplateAccess[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setTemplateAccess([]);
      setLoading(false);
      return;
    }

    const fetchSubscriptionData = async () => {
      try {
        // Fetch subscription data
        const { data: subData, error: subError } = await supabase
          .from('stripe_user_subscriptions')
          .select('*')
          .maybeSingle();

        if (subError) {
          console.error('Error fetching subscription:', subError);
        } else {
          setSubscription(subData);
        }

        // Fetch template access data
        const { data: accessData, error: accessError } = await supabase
          .from('template_access')
          .select('*')
          .gte('expires_at', new Date().toISOString());

        if (accessError) {
          console.error('Error fetching template access:', accessError);
        } else {
          setTemplateAccess(accessData || []);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, [user]);

  const getActiveProduct = () => {
    if (!subscription?.price_id) return null;
    return getProductByPriceId(subscription.price_id);
  };

  const isActive = () => {
    return subscription?.subscription_status === 'active';
  };

  const isPending = () => {
    return subscription?.subscription_status === 'incomplete' || 
           subscription?.subscription_status === 'not_started';
  };
  
  const hasAccessToTemplate = (templateId: string) => {
    // Check if user has active subscription
    if (isActive()) {
      return true;
    }
    
    // Check if user has specific template access
    return templateAccess.some(
      access => access.template_id === templateId && new Date(access.expires_at) > new Date()
    );
  };
  
  const getTemplateAccessExpiration = (templateId: string): Date | null => {
    const access = templateAccess.find(a => a.template_id === templateId);
    return access ? new Date(access.expires_at) : null;
  };

  return {
    subscription,
    templateAccess,
    loading,
    getActiveProduct,
    isActive,
    isPending,
    hasAccessToTemplate,
    getTemplateAccessExpiration,
  };
}